import { type InvoiceListParams, type InvoiceStatus, type InvoiceSummary, api } from '@/api/client'
import { CurrencyAmount } from '@/components/shared/CurrencyAmount'
import { EmptyState } from '@/components/shared/EmptyState'
import { InvoiceStatusBadge } from '@/components/shared/InvoiceStatusBadge'
import { PageHeader } from '@/components/shared/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable, type DataTableColumn } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useInfiniteQuery } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import type { NavigateOptions } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Plus, Search, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route as appLayoutRoute } from '../layout'

interface InvoicesListSearch {
  customer_id?: string
}

export const Route = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/finance/invoices',
  validateSearch: (s: Record<string, unknown>): InvoicesListSearch => ({
    customer_id: typeof s.customer_id === 'string' ? s.customer_id : undefined,
  }),
  component: InvoicesListPage,
})

const STATUS_OPTS: InvoiceStatus[] = ['draft', 'issued', 'partial', 'paid', 'overdue', 'voided']

function InvoicesListPage() {
  const { t } = useTranslation('finance')
  const navigate = useNavigate()
  const { customer_id: customerId } = Route.useSearch() as unknown as InvoicesListSearch

  const [status, setStatus] = useState<'' | InvoiceStatus>('')
  const [q, setQ] = useState('')

  const filters: InvoiceListParams = useMemo(
    () => ({
      status: status || undefined,
      q: q.trim() || undefined,
      customer_id: customerId || undefined,
      limit: 25,
    }),
    [status, q, customerId],
  )

  const query = useInfiniteQuery({
    queryKey: ['finance', 'invoices', filters],
    queryFn: ({ pageParam }) =>
      api.finance.invoices.list({ ...filters, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined,
  })

  const rows: InvoiceSummary[] = useMemo(
    () => query.data?.pages.flatMap((p) => p.data) ?? [],
    [query.data],
  )

  const columns: DataTableColumn<InvoiceSummary>[] = [
    {
      key: 'ref',
      header: t('invoice.ref'),
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{r.ref}</span>
          {r.auto_generated ? (
            <Sparkles
              className="h-3 w-3 text-muted-foreground"
              aria-label={t('invoice.auto_generated')}
            />
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      header: t('filters.status'),
      cell: (r) => <InvoiceStatusBadge status={r.status} />,
    },
    {
      key: 'issue_date',
      header: t('invoice.issue_date'),
      cell: (r) => <span className="tabular-nums">{r.issue_date}</span>,
    },
    {
      key: 'due_date',
      header: t('invoice.due_date'),
      cell: (r) => <span className="tabular-nums">{r.due_date}</span>,
    },
    {
      key: 'total',
      header: t('invoice.total'),
      align: 'right',
      cell: (r) => <CurrencyAmount centimes={r.total} size="sm" />,
    },
    {
      key: 'balance',
      header: t('invoice.balance'),
      align: 'right',
      cell: (r) => <CurrencyAmount centimes={r.balance} size="sm" />,
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title={t('invoices')} description={t('description')} />
        <Link to="/finance/invoices/new">
          <Button>
            <Plus className="h-4 w-4" />
            {t('invoice.new', { defaultValue: 'Nouvelle facture' })}
          </Button>
        </Link>
      </div>

      {customerId ? (
        <div className="flex items-center gap-2 rounded-xl border border-border bg-muted px-4 py-2 text-sm text-muted-foreground">
          {t('filters.filtered_by_customer', { defaultValue: 'Filtré par client' })}
          <Link
            to="/finance/invoices"
            className="font-medium text-foreground underline-offset-2 hover:underline"
          >
            {t('filters.clear', { defaultValue: 'Retirer le filtre' })}
          </Link>
        </div>
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('filters.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setStatus((e.target.value as InvoiceStatus) || '')}
          >
            <option value="">{t('filters.all')}</option>
            {STATUS_OPTS.map((s) => (
              <option key={s} value={s}>
                {t(`status.${s}`)}
              </option>
            ))}
          </Select>
        </CardContent>
      </Card>

      {query.isLoading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={t('invoice.empty')} />
      ) : (
        <>
          <DataTable<InvoiceSummary>
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            onRowClick={(r) =>
              navigate({
                to: `/finance/invoices/${r.id}`,
              } as unknown as NavigateOptions)
            }
          />
          {query.hasNextPage ? (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                disabled={query.isFetchingNextPage}
                onClick={() => query.fetchNextPage()}
              >
                {query.isFetchingNextPage ? '…' : t('load_more')}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
